import React from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Checkbox, IconButton, Input, TableCell } from '@mui/material';
import { red } from '@mui/material/colors';

import { actionColumnWidth, ColumnData, RowBase, RowEdits, selectionColumnWidth } from './priceGridTypes';
import { getValidationErrorsByColumnKey } from './priceGridUtilities';

interface Props<TRow extends RowBase> {
  readonly row: TRow;
  readonly columns: readonly ColumnData<TRow>[];
  readonly editableColumns: readonly ColumnData<TRow>[];
  readonly hasEditPermission: boolean;
  readonly isSelected: boolean;
  readonly rowEdits: RowEdits | undefined;
  readonly onSelect: (selected: boolean, shiftKey: boolean) => void;
  readonly onStartEditing: () => void;
  readonly onEditChange: (columnKey: string, value: string) => void;
  readonly onSave: () => void;
  readonly onCancelEditing: () => void;
}

export function PriceGridRow<TRow extends RowBase>({
  row,
  columns,
  editableColumns,
  hasEditPermission,
  isSelected,
  rowEdits,
  onSelect,
  onStartEditing,
  onEditChange,
  onSave,
  onCancelEditing,
}: Props<TRow>) {
  const isEditing = rowEdits != null;
  const validationErrorsByColumnKey = getValidationErrorsByColumnKey(editableColumns, rowEdits ?? {});
  const hasInvalidEdits = Object.values(validationErrorsByColumnKey).some(Boolean);

  return (
    <>
      <TableCell
        sx={{
          padding: 0,
          textAlign: 'center',
          width: selectionColumnWidth,
          '& .row-select': {
            opacity: isSelected ? 1 : 0,
            pointerEvents: isSelected ? 'auto' : 'none',
          },
          '@media (hover: none), (pointer: coarse)': {
            '& .row-select': {
              opacity: 1,
              pointerEvents: 'auto',
            },
          },
          'tr:hover & .row-select, & .row-select:focus-within': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        }}
      >
        <Checkbox
          checked={isSelected}
          className="row-select"
          disabled={!hasEditPermission}
          size="small"
          title="Select row"
          onChange={(event, checked) => {
            onSelect(checked, event.nativeEvent instanceof MouseEvent && event.nativeEvent.shiftKey);
          }}
        />
      </TableCell>
      {columns.map((column) => (
        <TableCell
          key={column.key}
          sx={
            isEditing
              ? { padding: 1 }
              : {
                  cursor: hasEditPermission ? 'pointer' : undefined,
                  overflow: 'hidden',
                  paddingX: 1,
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }
          }
          onClick={() => {
            if (!hasEditPermission || isEditing) return;
            onStartEditing();
          }}
        >
          {isEditing ? (
            <Input
              disabled={!column.getUpdatedRowWithEdit}
              error={Boolean(validationErrorsByColumnKey[column.key])}
              value={
                rowEdits[column.key] != null
                  ? rowEdits[column.key]
                  : column.getEditValue
                    ? column.getEditValue(row)
                    : column.getCellValue(row)
              }
              onChange={(event) => onEditChange(column.key, event.target.value)}
            />
          ) : (
            column.getCellValue(row)
          )}
        </TableCell>
      ))}
      <TableCell
        sx={{
          width: actionColumnWidth,
          textAlign: 'right',
          paddingY: 0,
          paddingX: 1,
          '& .row-actions': isEditing ? { opacity: 1, pointerEvents: 'auto' } : { opacity: 0, pointerEvents: 'none' },
          'tr:hover & .row-actions, & .row-actions:focus-within': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        }}
      >
        {isEditing ? (
          <Box className="row-actions">
            <IconButton disabled={hasInvalidEdits} onClick={onSave} color="primary" title="Save changes">
              <SaveIcon />
            </IconButton>
            <IconButton onClick={onCancelEditing} sx={{ color: red[500] }} title="Cancel changes">
              <CancelIcon />
            </IconButton>
          </Box>
        ) : (
          <IconButton className="row-actions" disabled={!hasEditPermission} onClick={onStartEditing} title="Edit row">
            <EditIcon />
          </IconButton>
        )}
      </TableCell>
    </>
  );
}
